import { useState } from 'react';
import {
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import BackgroundTexture from '../components/common/BackgroundTexture';
import Particles from '../components/common/Particles';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredContact: 'email'
  });

  const supportOptions = [
    {
      title: "24/7 Customer Support",
      description: "Our team is available around the clock to assist you",
      icon: ClockIcon,
      color: "text-primary-600"
    },
    {
      title: "Dedicated Account Team",
      description: "Personal bankers ready to help with your financial needs",
      icon: UserGroupIcon,
      color: "text-primary-600"
    },
    {
      title: "Secure Communication",
      description: "Your information is always protected",
      icon: ShieldCheckIcon,
      color: "text-primary-600"
    }
  ];

  const contactChannels = [
    {
      method: "Email Support",
      value: "support@firstequitycu.com",
      icon: EnvelopeIcon,
      description: "Send us your inquiries anytime",
      availability: "Response within 24 hours"
    },
    {
      method: "Phone Support",
      value: "1-800-First Equity",
      icon: PhoneIcon,
      description: "Speak directly with our team",
      availability: "24/7 Support Available"
    },
    {
      method: "Live Chat",
      value: "Available Online",
      icon: ChatBubbleLeftRightIcon,
      description: "Instant support through our chat system",
      availability: "Monday-Friday, 9AM-6PM"
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log(formData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <BackgroundTexture />
      <Particles />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-24 overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="absolute inset-0 bg-pattern-dots opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-radial from-primary-400/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 relative z-10 font-serif">
              We're Here to Help
              <span className="block mt-2 text-accent-300">
                Every Step of the Way
              </span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Get in touch with our expert team for personalized assistance with your banking needs.
              We're committed to providing you with the best possible service.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Channels */}
      <div className="py-24 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactChannels.map((channel) => (
              <div key={channel.method} 
                className="group bg-white rounded-2xl shadow-soft hover:shadow-xl 
                  transition-all duration-500 overflow-hidden border border-gray-100"
              >
                <div className={`p-8 bg-gradient-to-br from-primary-50 to-white`}>
                  <channel.icon className={`h-12 w-12 text-primary-600`} />
                  <h3 className="text-2xl font-bold mt-6 text-gray-900">{channel.method}</h3>
                  <p className="mt-3 text-gray-600">{channel.description}</p>
                  <div className="mt-6 inline-block bg-primary-50 rounded-full px-4 py-1 
                    text-sm font-medium text-primary-600">
                    {channel.value}
                  </div>
                </div>
                <div className="p-6 bg-gray-50">
                  <p className="text-sm text-gray-600">{channel.availability}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="py-24 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-serif">
                Send Us a Message
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Fill out the form below and we'll get back to you as soon as possible
              </p>
            </div>

            <form onSubmit={handleSubmit} 
              className="bg-gradient-to-br from-primary-50 to-white p-8 md:p-12 rounded-2xl shadow-soft">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 
                      focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-colors"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 
                      focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-colors"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <select
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 
                    focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-colors"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="account">Account Support</option>
                  <option value="loan">Loan Information</option>
                  <option value="technical">Technical Support</option>
                </select>
              </div>

              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 
                    focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-colors"
                  rows="4"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                ></textarea>
              </div>

              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Preferred Contact Method
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contact"
                      value="email"
                      checked={formData.preferredContact === 'email'}
                      onChange={(e) => setFormData({...formData, preferredContact: e.target.value})}
                      className="text-primary-600 focus:ring-primary-600/20"
                    />
                    <span className="ml-2 text-gray-700">Email</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contact"
                      value="phone"
                      checked={formData.preferredContact === 'phone'}
                      onChange={(e) => setFormData({...formData, preferredContact: e.target.value})}
                      className="text-primary-600 focus:ring-primary-600/20"
                    />
                    <span className="ml-2 text-gray-700">Phone</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="mt-8 w-full inline-flex items-center justify-center px-6 py-3 rounded-xl 
                  bg-primary-600 text-white hover:bg-primary-700 transition-all duration-300 
                  group"
              >
                Send Message
                <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Support Features */}
      <div className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportOptions.map((option) => (
              <div key={option.title} 
                className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-xl 
                  transition-all duration-500 border border-gray-100"
              >
                <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl 
                  inline-block">
                  <option.icon className={`h-8 w-8 ${option.color}`} />
                </div>
                <h3 className="text-xl font-bold mt-6 mb-3 text-gray-900">{option.title}</h3>
                <p className="text-gray-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage; 